import { cva, type VariantProps } from "../../lib/cva";
import type React from "react";
import type { Omittable } from "../../lib/props";
import { cn } from "../../lib/utils";
import { Icon, type IconName } from "./icon";
import { Text } from "./text";

export const emptyMediaVariants = cva(
  "flex shrink-0 items-center justify-center [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    defaultVariants: {
      variant: "default",
    },
    variants: {
      variant: {
        default: "bg-transparent",
        icon: "relative flex size-9 shrink-0 items-center justify-center rounded-md border bg-card not-dark:bg-clip-padding text-foreground shadow-sm/5 before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-md)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] dark:before:shadow-[0_-1px_--theme(--color-white/6%)] [&_svg:not([class*='size-'])]:size-4.5",
        // A quiet decorative well — `muted` fill, no chrome, no ghost cards.
        // For empties inside a component (a widget body, a small panel) where
        // the `icon` card-chip art would compete with the container's own
        // card chrome.
        circle:
          "relative flex size-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground [&_svg:not([class*='size-'])]:size-5",
      },
    },
  },
);

// The positioning wrapper around the media, keyed on the same `variant`
// axis: the `icon` chip fan needs breathing room below; the flat `circle`
// well sits closer to its title.
export const emptyMediaWrapperVariants = cva("relative", {
  defaultVariants: {
    variant: "default",
  },
  variants: {
    variant: {
      default: "mb-6",
      icon: "mb-6",
      circle: "mb-2",
    },
  },
});

export function Empty({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      className={cn(
        "flex min-w-0 flex-1 flex-col items-center justify-center gap-6 text-balance px-6 py-12 text-center md:py-20",
        className,
      )}
      data-slot="empty"
      {...props}
    />
  );
}

export function EmptyHeader({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      className={cn(
        "flex max-w-sm flex-col items-center text-center",
        className,
      )}
      data-slot="empty-header"
      {...props}
    />
  );
}

export function EmptyMedia({
  className,
  variant = "default",
  children,
  ...props
}: React.ComponentProps<"div"> &
  VariantProps<typeof emptyMediaVariants>): React.ReactElement {
  return (
    <div
      className={cn(emptyMediaWrapperVariants({ variant }), className)}
      data-slot="empty-media"
      data-variant={variant}
      {...props}
    >
      {variant === "icon" && (
        <>
          <div
            aria-hidden="true"
            className={cn(
              emptyMediaVariants({ className, variant }),
              "pointer-events-none absolute bottom-px origin-bottom-left -translate-x-0.5 -rotate-10 scale-84 shadow-none",
            )}
          />
          <div
            aria-hidden="true"
            className={cn(
              emptyMediaVariants({ className, variant }),
              "pointer-events-none absolute bottom-px origin-bottom-right translate-x-0.5 rotate-10 scale-84 shadow-none",
            )}
          />
        </>
      )}
      <div className={cn(emptyMediaVariants({ className, variant }))}>
        {children}
      </div>
    </div>
  );
}

export function EmptyTitle({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      className={cn("font-heading font-semibold text-xl", className)}
      data-slot="empty-title"
      {...props}
    />
  );
}

export function EmptyDescription({
  className,
  ...props
}: React.ComponentProps<"p">): React.ReactElement {
  return (
    <div
      className={cn(
        "text-muted-foreground text-sm [&>a:hover]:text-brand [&>a]:underline [&>a]:underline-offset-4 [[data-slot=empty-title]+&]:mt-1",
        className,
      )}
      data-slot="empty-description"
      {...props}
    />
  );
}

export function EmptyContent({
  className,
  ...props
}: React.ComponentProps<"div">): React.ReactElement {
  return (
    <div
      className={cn(
        "flex w-full min-w-0 max-w-sm flex-col items-center gap-4 text-balance text-sm",
        className,
      )}
      data-slot="empty-content"
      {...props}
    />
  );
}

export const emptyStateVariants = cva("", {
  defaultVariants: { size: "default" },
  variants: {
    // The three placement rungs. `default` fills a page or dialog section.
    // `sm` compacts the stock paddings to sit inside a widget card, popover
    // or small panel. `xs` is the row tier — a quiet text pair at list/section
    // scale; it renders no media and no actions regardless of what is passed.
    size: {
      default: "",
      sm: "gap-2 px-4 py-6 md:py-6",
      xs: "gap-1 px-4 py-3 md:py-3",
    },
  },
});

export type EmptyStateSize = NonNullable<
  VariantProps<typeof emptyStateVariants>["size"]
>;

const EMPTY_STATE_TITLE_CLASS: Record<EmptyStateSize, string | undefined> = {
  default: undefined,
  // Below page scale the title reads as muted body copy, not a headline.
  sm: "text-sm font-medium text-muted-foreground",
  xs: "text-sm font-medium text-muted-foreground",
};

const EMPTY_STATE_DESCRIPTION_CLASS: Record<EmptyStateSize, string | undefined> =
  {
    default: undefined,
    sm: "max-w-[280px]",
    xs: "max-w-[280px] text-xs",
  };

/**
 * The single-call empty state. Composes the parts above so call sites do not
 * re-decide composition; the parts stay public for genuinely bespoke states
 * (the app's error boundaries with their dev-only stack well).
 *
 * `media` picks the glyph treatment: `icon` is the fanned card chip for
 * designed first-run empties on a page surface; `circle` is the quiet muted
 * well for states inside a container that already owns card chrome (tables,
 * widgets, popovers). There is deliberately no bare-glyph member — the
 * un-contained icon read as unfinished and is retired. Omit `media` for a
 * text-only state.
 *
 * `color` reaches the glyph only: `destructive` for load/render failures,
 * `warning` for attention states. Copy stays in the title/description.
 */
export function EmptyState({
  size = "default",
  media,
  icon,
  color,
  title,
  description,
  detail,
  actions,
  className,
}: Omittable<{
  size?: EmptyStateSize;
  media?: "icon" | "circle";
  /** Glyph inside the media. Required with `media`, ignored without it. */
  icon?: IconName;
  color?: "destructive" | "warning";
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** One mono line under the description — an error message, never prose. */
  detail?: string;
  /** Real Buttons; seated side by side in one centered row that wraps. */
  actions?: React.ReactNode;
  className?: string;
}>): React.ReactElement {
  const xs = size === "xs";
  return (
    <Empty className={cn(emptyStateVariants({ size }), className)}>
      <EmptyHeader>
        {!xs && media === "icon" && icon ? (
          <EmptyMedia variant="icon">
            <Icon color={color} name={icon} size="lg" />
          </EmptyMedia>
        ) : null}
        {!xs && media === "circle" && icon ? (
          <EmptyMedia
            className={size === "sm" ? "size-8" : undefined}
            variant="circle"
          >
            <Icon
              color={color ?? "muted"}
              name={icon}
              size={size === "sm" ? "sm" : "2xl"}
            />
          </EmptyMedia>
        ) : null}
        {title != null ? (
          <EmptyTitle className={EMPTY_STATE_TITLE_CLASS[size]}>
            {title}
          </EmptyTitle>
        ) : null}
        {description != null ? (
          <EmptyDescription className={EMPTY_STATE_DESCRIPTION_CLASS[size]}>
            {description}
          </EmptyDescription>
        ) : null}
        {detail ? (
          <Text className="mt-1.5" mono size="xs" truncate={2} variant="faint">
            {detail}
          </Text>
        ) : null}
      </EmptyHeader>
      {/* `EmptyContent` stacks its children (actions above secondary help),
          so the actions get a row of their own: a primary beside its escape
          hatch reads as one choice, stacked they read as two steps. */}
      {!xs && actions ? (
        <EmptyContent>
          <div className="flex w-full flex-row flex-wrap justify-center gap-2">
            {actions}
          </div>
        </EmptyContent>
      ) : null}
    </Empty>
  );
}
